/**
 * Tela /catalog (TH-53357 / M5.E5.S1.1).
 *
 * Lista paginada do catálogo mestre (`catalog_products`). Tabela com
 * thumb 40x40 + EAN + nome + marca + categoria + qualidade + ações.
 * Filtros por qualidade (`pending` / `approved_*` / `rejected` /
 * `not_available`), categoria e busca (q). Bulk actions (selecionar
 * vários + aprovar / marcar não disponível em lote) — backend ainda
 * não tem endpoint bulk, então aplica chamadas em paralelo (P(promise)
 * com erro consolidado).
 *
 * ACL: catalog_operator e super_admin (backend bloqueia com 403, aqui
 * mostramos tela de "acesso negado" para os demais).
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { AppLayout } from "@/components/AppLayout";
import { RequireAuth } from "@/components/RequireAuth";
import { Button } from "@/components/ui/Button";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable";
import {
  FilterPills,
  type FilterPillOption,
} from "@/components/ui/FilterPills";
import { Input } from "@/components/ui/Input";
import { Pagination } from "@/components/ui/Pagination";
import { StatusPill } from "@/components/ui/StatusPill";
import { useAuth } from "@/hooks/useAuth";

interface CatalogProduct {
  id: string;
  ean: string | null;
  name: string;
  brand: string | null;
  category_slug: string | null;
  unit: string | null;
  master_image_url: string | null;
  master_image_quality: string; // pending|approved_manual|approved_auto|rejected|not_available
  ai_hint: string | null;
  source: string | null;
}

interface PagedCatalog {
  data: CatalogProduct[];
  meta: { page: number; per_page: number; total: number };
}

const PER_PAGE = 25;
const ALL_QUALITY = "__all__";

const QUALITY_FILTERS: readonly FilterPillOption<string>[] = [
  { label: "Todos", value: ALL_QUALITY },
  { label: "Pendentes", value: "pending" },
  { label: "Aprovados (auto)", value: "approved_auto" },
  { label: "Aprovados (manual)", value: "approved_manual" },
  { label: "Rejeitados", value: "rejected" },
  { label: "Sem imagem", value: "not_available" },
];

const QUALITY_LABEL: Record<string, string> = {
  pending: "Pendente",
  approved_auto: "Auto",
  approved_manual: "Manual",
  rejected: "Rejeitado",
  not_available: "Sem imagem",
};

const QUALITY_TONE: Record<string, "success" | "warn" | "destructive" | "muted"> = {
  pending: "warn",
  approved_auto: "success",
  approved_manual: "success",
  rejected: "destructive",
  not_available: "muted",
};

async function fetchCatalog(
  page: number,
  q: string,
  quality: string,
): Promise<PagedCatalog> {
  const params = new URLSearchParams({
    page: String(page),
    per_page: String(PER_PAGE),
  });
  if (q.trim()) params.set("q", q.trim());
  if (quality !== ALL_QUALITY) params.set("status", quality);
  const res = await fetch(`/api/catalog?${params.toString()}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as PagedCatalog;
}

async function bulkApprove(ids: readonly string[]): Promise<void> {
  // Backend não tem endpoint bulk — paraleliza chamadas. Promise.allSettled
  // pra não abortar no primeiro erro (operador prefere ver lista parcial
  // do que perder o lote inteiro).
  const results = await Promise.allSettled(
    ids.map((id) =>
      fetch(`/api/catalog/${id}/approve`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ decision: "approve" }),
      }).then((r) => {
        if (!r.ok) throw new Error(`${id} HTTP ${r.status}`);
        return r;
      }),
    ),
  );
  const failed = results.filter((r) => r.status === "rejected");
  if (failed.length) {
    throw new Error(
      `${failed.length}/${ids.length} falharam ao aprovar (operador deve refazer manualmente).`,
    );
  }
}

async function bulkMarkNotAvailable(ids: readonly string[]): Promise<void> {
  const results = await Promise.allSettled(
    ids.map((id) =>
      fetch(`/api/catalog/${id}/mark-not-available`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      }).then((r) => {
        if (!r.ok) throw new Error(`${id} HTTP ${r.status}`);
        return r;
      }),
    ),
  );
  const failed = results.filter((r) => r.status === "rejected");
  if (failed.length) {
    throw new Error(`${failed.length}/${ids.length} falharam.`);
  }
}

export function CatalogPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canRead =
    user?.role === "super_admin" || user?.role === "catalog_operator";

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  const [quality, setQuality] = useState<string>(ALL_QUALITY);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Debounce do search.
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset seleção quando filtros mudam — itens podem sumir da página.
  useEffect(() => {
    setSelected(new Set());
  }, [page, q, quality]);

  const catalogQuery = useQuery({
    queryKey: ["catalog", page, q, quality],
    queryFn: () => fetchCatalog(page, q, quality),
    staleTime: 30_000,
    enabled: canRead,
  });

  const approveMutation = useMutation({
    mutationFn: bulkApprove,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["catalog"] });
      setSelected(new Set());
    },
  });

  const markNAMutation = useMutation({
    mutationFn: bulkMarkNotAvailable,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["catalog"] });
      setSelected(new Set());
    },
  });

  const items = useMemo(
    () => catalogQuery.data?.data ?? [],
    [catalogQuery.data],
  );

  const allSelectedOnPage = items.length > 0 && items.every((i) => selected.has(i.id));

  if (user && !canRead) {
    return (
      <RequireAuth>
        <AppLayout breadcrumb="CATÁLOGO / MESTRE" title="Acesso negado">
          <div className="mx-auto max-w-3xl">
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Esta tela é exclusiva de super_admin e catalog_operator.
            </p>
          </div>
        </AppLayout>
      </RequireAuth>
    );
  }

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      if (allSelectedOnPage) {
        const next = new Set(prev);
        items.forEach((i) => next.delete(i.id));
        return next;
      }
      const next = new Set(prev);
      items.forEach((i) => next.add(i.id));
      return next;
    });
  };

  const columns: DataTableColumn<CatalogProduct>[] = [
    {
      key: "select",
      header: (
        <input
          type="checkbox"
          aria-label="Selecionar todos"
          checked={allSelectedOnPage}
          onChange={toggleAll}
          className="h-4 w-4 cursor-pointer"
        />
      ) as unknown as string,
      width: "44px",
      cell: (cp) => (
        <input
          type="checkbox"
          aria-label={`Selecionar ${cp.name}`}
          checked={selected.has(cp.id)}
          onChange={() => toggleOne(cp.id)}
          onClick={(e) => e.stopPropagation()}
          className="h-4 w-4 cursor-pointer"
        />
      ),
    },
    {
      key: "thumb",
      header: "",
      width: "56px",
      cell: (cp) =>
        cp.master_image_url ? (
          <img
            src={cp.master_image_url}
            alt=""
            loading="lazy"
            className="h-10 w-10 rounded object-cover"
          />
        ) : (
          <div className="h-10 w-10 rounded bg-[var(--color-muted)]" />
        ),
    },
    {
      key: "ean",
      header: "EAN",
      mono: true,
      width: "140px",
      cell: (cp) => (
        <span className="text-[var(--color-muted-foreground)]">{cp.ean ?? "—"}</span>
      ),
    },
    {
      key: "name",
      header: "Nome",
      cell: (cp) => (
        <Link
          to="/catalog/$cpId"
          params={{ cpId: cp.id }}
          className="font-medium text-[var(--color-foreground)] hover:text-[var(--color-primary)] hover:underline"
        >
          {cp.name}
        </Link>
      ),
    },
    {
      key: "brand",
      header: "Marca",
      cell: (cp) => (
        <span className="text-[var(--color-muted-foreground)]">
          {cp.brand ?? "—"}
        </span>
      ),
    },
    {
      key: "category_slug",
      header: "Categoria",
      cell: (cp) => (
        <span className="text-[var(--color-muted-foreground)]">
          {cp.category_slug ?? "—"}
        </span>
      ),
    },
    {
      key: "master_image_quality",
      header: "Qualidade",
      width: "140px",
      cell: (cp) => (
        <StatusPill
          tone={QUALITY_TONE[cp.master_image_quality] ?? "muted"}
        >
          {QUALITY_LABEL[cp.master_image_quality] ?? cp.master_image_quality}
        </StatusPill>
      ),
    },
  ];

  const total = catalogQuery.data?.meta.total ?? 0;
  const selectedCount = selected.size;
  const bulkBusy = approveMutation.isPending || markNAMutation.isPending;

  const headerActions = (
    <div className="flex items-center gap-2">
      <Link to="/catalog/queue">
        <Button size="sm">Fila do operador →</Button>
      </Link>
    </div>
  );

  return (
    <RequireAuth>
      <AppLayout
        breadcrumb="CATÁLOGO / MESTRE"
        title="Catálogo"
        actions={headerActions}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex w-full max-w-md flex-col gap-2 sm:flex-row sm:items-center">
              <Input
                placeholder="Buscar por nome, EAN ou marca…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                aria-label="Buscar no catálogo"
              />
            </div>
            <FilterPills
              options={QUALITY_FILTERS}
              value={quality}
              onChange={(v) => {
                setQuality(v);
                setPage(1);
              }}
            />
          </div>

          {selectedCount > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-muted)] px-4 py-2 text-sm">
              <span>
                <strong>{selectedCount}</strong> selecionado
                {selectedCount > 1 ? "s" : ""}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="primary"
                  disabled={bulkBusy}
                  onClick={() =>
                    approveMutation.mutate(Array.from(selected))
                  }
                >
                  Aprovar lote
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={bulkBusy}
                  onClick={() =>
                    markNAMutation.mutate(Array.from(selected))
                  }
                >
                  Marcar sem imagem
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={bulkBusy}
                  onClick={() => setSelected(new Set())}
                >
                  Limpar seleção
                </Button>
              </div>
            </div>
          )}

          {approveMutation.error && (
            <div role="alert" className="text-sm text-[var(--color-destructive)]">
              {(approveMutation.error as Error).message}
            </div>
          )}
          {markNAMutation.error && (
            <div role="alert" className="text-sm text-[var(--color-destructive)]">
              {(markNAMutation.error as Error).message}
            </div>
          )}

          <DataTable
            columns={columns}
            rows={items}
            rowKey={(c) => c.id}
            emptyMessage={
              catalogQuery.isLoading ? "Carregando…" : "Nenhum produto encontrado."
            }
          />

          <Pagination
            page={page}
            perPage={PER_PAGE}
            total={total}
            onPageChange={setPage}
          />
        </div>
      </AppLayout>
    </RequireAuth>
  );
}
